import { Calendar, Layers, MoreHorizontal, Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { getListExamSchedulesQueryKey, useListExamSchedules } from '@/api/generated/exams';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ExamOfferingList from './ExamOfferingList';

export default function ExamList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear() + 1);
  const [activeTab, setActiveTab] = useState('schedules');

  const { data: result, isLoading: loading } = useListExamSchedules(
    {
      year: selectedYear,
    },
    {
      query: {
        enabled: activeTab === 'schedules',
        queryKey: getListExamSchedulesQueryKey({ year: selectedYear }),
      },
    },
  );

  const exams = result?.data || [];

  const getExamTypeLabel = (type: string) => {
    return t(`modules.exams.types.${type}`, type);
  };

  const getCategoryBadge = (cat: string) => {
    const label = t(`modules.exams.categoryBadges.${cat}`, cat);
    switch (cat) {
      case 'MAIN':
        return (
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
            {label}
          </Badge>
        );
      case 'MAKEUP':
        return (
          <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
            {label}
          </Badge>
        );
      case 'RETAKE':
        return (
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
            {label}
          </Badge>
        );
      default:
        return null;
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">入試設定管理</h1>
          <p className="text-gray-500 mt-1">入試の日程、区分、および募集定員を一元管理します。</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-8">
          <TabsTrigger value="schedules" className="gap-2">
            <Calendar className="w-4 h-4" />
            入試区分・日程設定
          </TabsTrigger>
          <TabsTrigger value="offerings" className="gap-2">
            <Layers className="w-4 h-4" />
            募集区分・定員設定
          </TabsTrigger>
        </TabsList>

        <TabsContent value="schedules" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">入試区分一覧</h2>
            <Button
              className="bg-blue-600 hover:bg-blue-700 shadow-md"
              onClick={() => navigate('/admin/master/exams/new')}
            >
              <Plus className="w-4 h-4 mr-2" /> {t('modules.exams.createSchedule')}
            </Button>
          </div>

          {/* Catalog Filter */}
          <div className="flex flex-col sm:flex-row justify-between gap-4 bg-gray-50 p-4 rounded-lg border">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Target Catalog:</span>
              <select
                className="border rounded px-2 py-1 text-sm bg-white"
                value={selectedYear} // Using year for now as mock, ideally use catalogId
                onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
              >
                <option value={2026}>2026年度 (Draft)</option>
                <option value={2025}>2025年度 (Published)</option>
              </select>
            </div>

            <div className="flex gap-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                <Input placeholder={t('common.search')} className="pl-9 w-[200px] h-9 bg-white" />
              </div>
            </div>
          </div>

          <Card className="shadow-sm border-t-4 border-t-blue-500">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[80px]">{t('modules.exams.fields.category')}</TableHead>
                    <TableHead>{t('modules.exams.fields.name')}</TableHead>
                    <TableHead>{t('modules.exams.fields.type')}</TableHead>
                    <TableHead>{t('modules.exams.fields.applicationPeriod')}</TableHead>
                    <TableHead>{t('modules.exams.fields.examDate')}</TableHead>
                    <TableHead className="text-right">{t('common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-10 text-gray-500">
                        {t('modules.exams.loading')}
                      </TableCell>
                    </TableRow>
                  ) : exams.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-10 text-gray-500">
                        {t('modules.exams.empty')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    exams.map((exam) => (
                      <TableRow key={exam.id} className="group hover:bg-gray-50 transition-colors cursor-pointer">
                        <TableCell>{getCategoryBadge(exam.category || 'MAIN')}</TableCell>
                        <TableCell>
                          <div className="font-medium text-gray-900">{exam.name}</div>
                          <div className="text-xs text-gray-500">{exam.slug}</div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="font-normal text-gray-700">
                            {getExamTypeLabel(exam.examType)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {exam.applicationStart} <span className="text-gray-400">~</span> {exam.applicationEnd}
                        </TableCell>
                        <TableCell className="text-sm">
                          <div className="flex items-center gap-1 text-gray-600">
                            <Calendar className="w-3.5 h-3.5" />
                            <span>
                              {exam.announcementDate
                                ? `${t('modules.exams.fields.announcement')}: ${exam.announcementDate}`
                                : '日程未定'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100">
                                <span className="sr-only">Open menu</span>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>{t('common.actions')}</DropdownMenuLabel>
                              <DropdownMenuItem asChild>
                                <Link to={`/admin/master/exams/${exam.id}`}>{t('common.edit')}</Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem>複製して作成</DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem className="text-red-600">{t('common.delete')}</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="offerings">
          {/* Embed the offering list logic here or just render the component */}
          {/* We'll assume ExamOfferingList is robust enough to be embedded, though it has its own header.
                Ideally, we pass a prop to hide header, but for now we just wrap it.
                Actually, simpler is to just render it. */}
          <div className="bg-gray-50/50 p-1 rounded-lg">
            <ExamOfferingList />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
